'use client';
import Link from 'next/link';

import { useState, type FormEvent } from 'react';

type NewsletterLeadFormProps = {
  idPrefix: string;
  sourceSlug: string;
  sourceName: string;
  offer: string;
  redirectPath: string;
  submitLabel?: string;
  variant: 'about' | 'shop';
};

type LeadResponse = {
  ok?: boolean;
  message?: string;
  error?: string;
  emailDelivery?: { status?: 'sent' | 'not_configured' | 'failed' };
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function NewsletterLeadForm({
  idPrefix,
  sourceSlug,
  sourceName,
  offer,
  redirectPath,
  submitLabel = 'Subscribe',
  variant,
}: NewsletterLeadFormProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [message, setMessage] = useState('No spam. Unsubscribe anytime.');
  const inputId = `${idPrefix}-email`;
  const messageId = `${idPrefix}-status`;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const cleanEmail = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(cleanEmail)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    try {
      setStatus('saving');
      setMessage('Saving your place...');
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: cleanEmail,
          consent: true,
          sourceSlug,
          sourceType: 'manual',
          sourceName,
          offer,
          redirectPath,
          website: String(form.get('website') || ''),
        }),
      });
      const payload = await response.json().catch(() => ({})) as LeadResponse;
      if (!response.ok || !payload.ok) throw new Error(payload.error || 'Unable to subscribe right now.');
      setEmail('');
      setStatus('saved');
      setMessage(payload.message || 'Request recorded. Email confirmation is required.');
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Unable to subscribe right now.');
    }
  }

  const isShop = variant === 'shop';
  return (
    <form onSubmit={handleSubmit} className={isShop ? 'flex flex-col justify-center gap-3 px-8 py-8' : 'grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]'}>
      <label className="sr-only" htmlFor={inputId}>Email address</label>
      <input name="website" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <div className={isShop ? 'flex gap-3' : 'contents'}>
        <input
          id={inputId}
          name="email"
          type="email"
          autoComplete="email"
          required
          aria-describedby={messageId}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="Your email address"
          className={isShop
            ? 'h-12 min-w-0 flex-1 rounded-[.45rem] border border-white bg-white px-4 text-sm text-[#2f2d2a] outline-none focus:border-[#5f8769]'
            : 'h-12 rounded-[.45rem] border border-[#ead8c8] bg-white px-5 text-[14px] text-[#2f2d2a] outline-none transition placeholder:text-[#9d8c80] focus:border-[#5f8769]'}
        />
        <button
          type="submit"
          disabled={status === 'saving'}
          className="h-12 rounded-[.45rem] bg-[#5f8769] px-7 text-[13px] font-semibold text-white transition hover:bg-[#547a5f] disabled:cursor-not-allowed disabled:opacity-65"
        >
          {status === 'saving' ? 'Subscribing...' : submitLabel}
        </button>
      </div>
      <p
        id={messageId}
        aria-live="polite"
        className={`${isShop ? '' : 'sm:col-span-2'} text-xs ${status === 'error' ? 'text-red-700' : status === 'saved' ? 'text-[#315d3f]' : 'text-[#5e554d]'}`}
      >
        {message}
      </p>
      <Link href="/newsletter" className="text-xs underline">Manage newsletter preferences</Link>
    </form>
  );
}
